import loadable from "@loadable/component";
import { Button } from "@tvg/fuse-design/buttons";
import { ContentPattern as FuseContentPattern } from "@tvg/fuse-design/content-pattern";
import { Link } from "@tvg/fuse-design/link";
import { GridContainer, GridItem, GridRow } from "@tvg/fuse-design/responsive-grid";
import { SpacingWrapper } from "@tvg/fuse-design/spacing-wrapper";
import { IconChevronRight } from "@tvg/fuse-icons";
import useGlobalConfig from "client/context/GlobalConfig";
import getEventHandler from "client/utils/eventHandler";
import getIconName from "client/utils/getIconName";
import getImageSources from "client/utils/getImageSources";
import React, { SVGAttributes, useMemo } from "react";
import { AmpContentPattern, AmpContentPatternCTA, AmpContentPatternLink } from "types/amplience";
import { iconColourConfig } from "./config";

export interface ContentPatternProps {
  config: AmpContentPattern;
}

const Icon = loadable<SVGAttributes<SVGElement> & { name: string }, typeof import("@tvg/fuse-icons")>(
  () => import(/* webpackChunkName: "FuseIcons" */ "@tvg/fuse-icons") as never,
  {
    resolveComponent: (m, { name }) => m[name as keyof typeof m],
  },
);

const VideoPlayer = loadable(() => import(/* webpackChunkName: "VideoPlayer" */ "../VideoPlayer/VideoPlayer"));
const TextBlock = loadable(() => import(/* webpackChunkName: "TextBlock" */ "client/components/TextBlock/TextBlock"), {
  resolveComponent: (m) => m.TextBlock,
});

const ContentPattern: React.FC<ContentPatternProps> = ({
  config: {
    brandLogo,
    content,
    icon,
    iconColour = "Black",
    image,
    imageRatio,
    imageAlignment,
    imagePosition,
    video,
    _meta,
  },
  ...props
}) => {
  const { brand } = useGlobalConfig();

  const buttons = content?.ctaContentPattern?.content?.cta as AmpContentPatternCTA[];
  const hasButton = !!buttons && buttons?.length > 0;
  const links = content?.ctaContentPattern?.content?.link as AmpContentPatternLink[];
  const hasLink = !!links && links?.length > 0;

  const configProps = useMemo(() => {
    return {
      brandLogo: brandLogo && {
        alt: `${brandLogo} logo`,
        brand: brandLogo,
      },
      icon: icon && <Icon name={getIconName(icon)} fill={iconColourConfig[iconColour]} />,
      imageWrapper: {
        imageAlignment: imageAlignment?.toLowerCase(),
        imagePosition: imagePosition?.toLowerCase(),
        imageRatio,
      },
      picture: image && {
        ...getImageSources(image, {
          brand,
          w: 740,
          qlt: "default",
          templates: ["poi"],
        }),
        alt: "",
        isLazy: true,
      },

      videoPlayerSlot: video && <VideoPlayer hideHeadlines ratio={imageRatio} gridWrapper={false} config={video} />,
    };
  }, [icon, brandLogo, iconColour, imageAlignment, imagePosition, imageRatio, image, brand, video]);

  const mainContentText = content?.mainContent?.mainContentText;

  const textBlockConfig = useMemo(
    () => ({
      _meta: _meta,
      text: mainContentText || "",
    }),
    [_meta, mainContentText],
  );

  return (
    <SpacingWrapper marginBottom="l">
      <GridContainer>
        <GridRow>
          <GridItem xxs="12" l="10" colStart-l="2">
            <FuseContentPattern dataTestId="content-content-pattern" {...props} {...configProps}>
              {mainContentText && <TextBlock config={textBlockConfig} gridWrapper={false} noSidePadding />}
              {hasButton &&
                buttons.map((button: AmpContentPatternCTA, i: number) => (
                  <Button
                    key={i}
                    onClick={getEventHandler(button.publishEvent)}
                    href={button.url}
                    size={{ xxs: "small", m: "medium", l: "large" }}
                    slot="cta"
                    type="button"
                    variant={i === 0 ? "secondary" : "tertiary"}
                  >
                    {button.text}
                  </Button>
                ))}
              {hasLink &&
                links.map((link: AmpContentPatternLink, i: number) => (
                  <Link key={i} href={link.url} icon={<IconChevronRight />} iconRight slot="cta">
                    {link.text}
                  </Link>
                ))}
            </FuseContentPattern>
          </GridItem>
        </GridRow>
      </GridContainer>
    </SpacingWrapper>
  );
};

export default React.memo(ContentPattern);
